import React from 'react';
import { object, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { StaticPage, TopbarContainer } from '..';
import {
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  MarketplaceProofStats,
  SectionCageTrailers,
  SectionBoxTrailers,
  SectionEnclosedTrailers,
  SectionCarTrailers,
  SectionSuburbTrailerTypeListings,
  SectionSuburbReviews,
  SectionSuburbSnapshot,
  SectionSuburbLocalInfo,
  MultiFilterSearchBar,
  NamedLink,
  NamedRedirect,
} from '../../components';
import { getSuburbTrailerTypeLandingPage } from '../../util/suburbTrailerTypeLandingPages';
import { getSuburbLandingSlugRedirect } from '../../util/suburbLandingSlugRedirects';
import {
  buildSuburbTrailerTypeBreadcrumbItems,
  createSuburbTrailerTypeBreadcrumbSchema,
} from '../../util/trailerHireBreadcrumbs';
import { MARKETPLACE_COMPLETED_HIRE_REVIEWS_COUNT_DISPLAY } from '../../marketplace-custom-config';
import { INVENTORY_SCOPE_AROUND } from '../../util/marketplaceProofStats';

import pageCss from '../SuburbLandingPage/SuburbLandingPage.module.css';
import css from './SuburbTrailerTypeLandingPage.module.css';

const TRAILER_TYPE_SECTIONS = {
  'cage-trailers': SectionCageTrailers,
  'box-trailers': SectionBoxTrailers,
  'enclosed-trailers': SectionEnclosedTrailers,
  'car-trailers': SectionCarTrailers,
};

const renderBreadcrumbLink = item => {
  if (item.current || !item.link) {
    return (
      <span className={pageCss.breadcrumbCurrent} aria-current="page">
        {item.label}
      </span>
    );
  }

  return (
    <NamedLink name={item.link.name} params={item.link.params} className={pageCss.breadcrumbLink}>
      {item.label}
    </NamedLink>
  );
};

export const SuburbTrailerTypeLandingPageComponent = props => {
  const { history, match, previewSnapshot = null, previewKey: statePreviewKey = null } = props;
  const citySlug = match?.params?.citySlug;
  const typeSlug = match?.params?.typeSlug;
  const redirectCitySlug = getSuburbLandingSlugRedirect(citySlug);

  if (redirectCitySlug) {
    return (
      <NamedRedirect
        name="SuburbTrailerTypeLandingPage"
        params={{ citySlug: redirectCitySlug, typeSlug }}
      />
    );
  }

  const pageConfig = getSuburbTrailerTypeLandingPage(citySlug, typeSlug);

  if (!pageConfig) {
    return <NamedRedirect name="NotFoundPage" />;
  }

  const {
    metaTitle,
    metaDescription,
    intro,
    typeName,
    typeSlug: resolvedTypeSlug,
    pubCategory,
    previewKey,
    suburb,
  } = pageConfig;
  const { name: cityName } = suburb;
  const TypeContentSection = TRAILER_TYPE_SECTIONS[resolvedTypeSlug];
  const breadcrumbItems = buildSuburbTrailerTypeBreadcrumbItems(suburb, resolvedTypeSlug);
  const breadcrumbSchema = createSuburbTrailerTypeBreadcrumbSchema(suburb, resolvedTypeSlug);
  const pageSchema = [
    {
      '@context': 'http://schema.org',
      '@type': 'WebPage',
      description: metaDescription,
      name: metaTitle,
    },
    breadcrumbSchema,
  ].filter(Boolean);
  const isCurrentTypePage = statePreviewKey === previewKey;
  const typeSnapshot = isCurrentTypePage ? previewSnapshot : null;

  return (
    <StaticPage title={metaTitle} description={metaDescription} schema={pageSchema}>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>

        <LayoutWrapperMain className={pageCss.root}>
          <div className={pageCss.hero}>
            {breadcrumbItems.length > 0 ? (
              <nav className={pageCss.breadcrumbs} aria-label="Breadcrumb">
                <ol className={pageCss.breadcrumbList}>
                  {breadcrumbItems.map((item, index) => (
                    <React.Fragment key={item.id}>
                      {index > 0 ? (
                        <li className={pageCss.breadcrumbSeparatorItem} aria-hidden="true">
                          <span className={pageCss.breadcrumbSeparator} />
                        </li>
                      ) : null}
                      <li className={pageCss.breadcrumbItem}>{renderBreadcrumbLink(item)}</li>
                    </React.Fragment>
                  ))}
                </ol>
              </nav>
            ) : null}

            <h1 className={pageCss.pageTitle}>
              {typeName} trailer hire in {cityName}
            </h1>
            <MarketplaceProofStats
              inventoryScope={INVENTORY_SCOPE_AROUND}
              listingCount={typeSnapshot?.totalCount}
              minPriceAmount={typeSnapshot?.minPriceAmount}
              currency={typeSnapshot?.currency || 'AUD'}
              locationName={cityName}
              trailerTypeName={typeName}
              reviewCountDisplay={MARKETPLACE_COMPLETED_HIRE_REVIEWS_COUNT_DISPLAY}
            />
            <p className={pageCss.intro}>{intro}</p>

            <section
              className={pageCss.searchSection}
              aria-label={`Search ${typeName.toLowerCase()} trailers in ${cityName}`}
            >
              <h2 className={pageCss.searchHeading}>
                Search {typeName.toLowerCase()} trailers near you
              </h2>
              <div className={pageCss.searchBar}>
                <MultiFilterSearchBar
                  history={history}
                  initialSuburb={suburb}
                  initialPubCategory={pubCategory}
                />
              </div>
            </section>
          </div>

          <ul className={pageCss.sections}>
            <li className={pageCss.section}>
              <div className={pageCss.sectionContent}>
                <SectionSuburbTrailerTypeListings pageConfig={pageConfig} />
              </div>
            </li>

            <li className={pageCss.section}>
              <div className={pageCss.sectionContent}>
                <SectionSuburbReviews
                  suburb={suburb}
                  previewKey={previewKey}
                  trailerType={typeSlug}
                />
              </div>
            </li>

            <li className={pageCss.section}>
              <div className={pageCss.sectionContent}>
                <SectionSuburbSnapshot
                  suburb={suburb}
                  previewKey={previewKey}
                  trailerTypeName={typeName}
                />

                {TypeContentSection ? (
                  <div className={css.typeContent}>
                    <TypeContentSection citySlug={citySlug} />
                  </div>
                ) : null}

                <SectionSuburbLocalInfo suburb={suburb} typeName={typeName} />
              </div>
            </li>
          </ul>
        </LayoutWrapperMain>

        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </StaticPage>
  );
};

SuburbTrailerTypeLandingPageComponent.propTypes = {
  history: object.isRequired,
  match: object.isRequired,
  previewSnapshot: object,
  previewKey: string,
};

const mapStateToProps = state => {
  const { previewSnapshot, previewKey } = state.SuburbTrailerTypeLandingPage || {};
  return {
    previewSnapshot,
    previewKey,
  };
};

const SuburbTrailerTypeLandingPage = compose(
  withRouter,
  connect(mapStateToProps)
)(SuburbTrailerTypeLandingPageComponent);

export default SuburbTrailerTypeLandingPage;
